import { Tag } from "antd";
import { mapUnitToAcronym, FACULTY_UNIT_ACRONYMS } from "./ouberUtils";

export function testFunction() {
  console.log("test");
}

export function findMenuItem(items, key) {
  for (const item of items) {
    if (item.key === key) {
      return item;
    }
    if (item.children) {
      const found = findMenuItem(item.children, key);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

export const applySortableColumns = (columns = []) => {
  return columns.map((col) => {
    // If column already has a custom sorter, don't override
    if (col.sorter) return col;

    // Allow both sortable:true OR filterable:true as triggers
    if (col.sortable) {
      return {
        ...col,
        sorter: (a, b) => {
          const key = col.dataIndex;
          const va = a?.[key];
          const vb = b?.[key];

          // String comparison
          if (typeof va === "string" && typeof vb === "string") {
            return va.localeCompare(vb);
          }

          // Number comparison
          if (typeof va === "number" && typeof vb === "number") {
            return va - vb;
          }

          // Fallback to equal
          return 0;
        },
        sortDirections: ["ascend", "descend"],
      };
    }

    return col;
  });
};

export function renderStatus(
  orangeWords,
  greenWords,
  redWords,
  purpleWords = [],
  magentaWords = []
) {
  return (status) => {
    let color;
    if (orangeWords.includes(status)) color = "orange";
    else if (greenWords.includes(status)) color = "green";
    else if (redWords.includes(status)) color = "red";
    else if (purpleWords.includes(status)) color = "purple";
    else if (magentaWords.includes(status)) color = "magenta";
    else color = "blue";

    return <Tag color={color}>{status.toUpperCase()}</Tag>;
  };
}

export function formatIsoDate(isoDate, includeTime = true) {
  const date = new Date(isoDate);
  if (includeTime === true) {
    return date.toLocaleString();
  }
  return date.toLocaleDateString();
}

export function parseEmployeeInfo(fullString, type) {
  try {
    if (typeof fullString !== "string" || typeof type !== "string") {
      throw new Error("Both arguments must be strings.");
    }

    const [positionPart, acronymPart] = fullString
      .split(" - ")
      .map((str) => str.trim());

    const officeAcronyms = {
      AO: "Accounting Office",
      OVCFA: "Office of the Vice Chancellor for Finance and Administration",
      FICS: "Faculty of Information and Communication Studies",
      CODTL: "Center for Open and Digital Teaching and Learning",
      FEd: "Faculty of Education",
      ICTDO: "Information & Communication Technology Development Office",
      FMDS: "Faculty of Management and Development Studies",
      BO: "Budget Office",
      OUR: "Office of the University Registrar",
      CDMO: "Campus Development and Maintenance Office",
      OVCAA: "Office of the Vice Chancellor for Academic Affairs",
      SPMO: "Supply and Property Management Office",
      CO: "Cash Office",
      OC: "Office of the Chancellor",
      HRDO: "Human Resources Development Office",
      QAO: "Quality Assurance Office",
      OPA: "Office of Public Affairs",
      Pahinungod: "Ugnayan ng Pahinungod/Oblation Corps Office",
      OGC: "Office of Gender Concerns",
      OSA: "Office of Student Affairs",
      OLC: "Office of the Legal Counsel",
      Library: "University Library",
    };

    switch (type.toLowerCase()) {
      case "position":
        return positionPart || "";
      case "unit":
        return officeAcronyms[acronymPart] || "";
      default:
        throw new Error(
          `Unknown type: ${type}. Expected 'position' or 'unit'.`
        );
    }
  } catch (error) {
    console.error("parseEmployeeInfo error:", error.message);
    return "";
  }
}

export function getMonthAndYear(dateString) {
  try {
    if (typeof dateString !== "string") {
      throw new Error("Input must be a string in YYYY-MM-DD format.");
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      throw new Error("Invalid date format.");
    }

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    return `${month} ${year}`;
  } catch (error) {
    console.error("getMonthAndYear error:", error.message);
    return "";
  }
}

export function formatName(firstname, middlename, lastname, format) {
  const hasMiddle = middlename && middlename.trim().length > 0;
  const middleInitial = hasMiddle ? middlename.trim()[0] + "." : "";

  switch (format.toLowerCase()) {
    case "fml":
      return hasMiddle
        ? `${firstname} ${middlename}, ${lastname}`
        : `${firstname}, ${lastname}`;
    case "fmcutl":
      return hasMiddle
        ? `${firstname} ${middleInitial} ${lastname}`
        : `${firstname} ${lastname}`;
    case "lfm":
      return hasMiddle
        ? `${lastname}, ${firstname} ${middlename}`
        : `${lastname}, ${firstname}`;
    case "lfmcut":
      return hasMiddle
        ? `${lastname}, ${firstname} ${middleInitial}`
        : `${lastname}, ${firstname}`;
    default:
      return null;
  }
}

export const extractPositionDates = (position = {}) => {
  const position_start_date =
    position?.position_start_date ?? position?.pivot?.position_start_date ?? null;
  const position_end_date =
    position?.position_end_date ?? position?.pivot?.position_end_date ?? null;

  return {
    position_start_date,
    position_end_date,
  };
};

export const toUiPosition = (position = {}) => {
  const { position_start_date, position_end_date } = extractPositionDates(position);

  return {
    ...position,
    position_start_date,
    position_end_date,
    is_inactive: isAssignmentInactiveByDate(position_end_date),
  };
};

export const formatAssignmentRange = (startDate, endDate) =>
  `${startDate || "-"} to ${endDate || "-"}`;

export const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeDateOnly = (value) => {
  if (!value) return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    return trimmed.length >= 10 ? trimmed.slice(0, 10) : trimmed;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return getLocalDateString(value);
  }

  return null;
};

export const isAssignmentInactiveByDate = (
  endDate,
  today = getLocalDateString()
) => Boolean(endDate) && endDate < today;

export const isEmployeeActiveByDate = (
  employmentEndDate,
  today = getLocalDateString()
) => {
  const normalizedEndDate = normalizeDateOnly(employmentEndDate);

  if (!normalizedEndDate) {
    return true;
  }

  return normalizedEndDate >= today;
};

const toOptionalBoolean = (value) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (value === 1 || value === "1" || value === "true") {
    return true;
  }

  if (value === 0 || value === "0" || value === "false") {
    return false;
  }

  return null;
};

// profile_photo is either a full external URL (Google avatar) or a
// site-relative upload path (/uploads/xxx.jpg) — only the latter needs
// the API base URL prefixed.
export const resolveProfilePhotoUrl = (profilePhoto) => {
  if (!profilePhoto) return null;
  if (/^https?:\/\//i.test(profilePhoto)) return profilePhoto;
  const baseUrl =
    process.env.REACT_APP_API_URL?.replace(/\/api$/, "") ??
    "http://localhost:8000";
  return `${baseUrl}${profilePhoto}`;
};

export const normalizeEmployeeStatus = (employee = {}) => {
  const normalizedStartDate = normalizeDateOnly(employee?.employment_start_date);
  const normalizedEndDate = normalizeDateOnly(employee?.employment_end_date);
  const computedIsActive = isEmployeeActiveByDate(normalizedEndDate);
  const backendIsActive = toOptionalBoolean(employee?.is_active);

  return {
    ...employee,
    employment_start_date: normalizedStartDate,
    employment_end_date: normalizedEndDate,
    is_active:
      backendIsActive === null || backendIsActive === undefined
        ? computedIsActive
        : backendIsActive,
  };
};

// FSR spectating only makes sense for Dean/Program Chair positions — they're
// the ones in the actual FSR approval chain. Faculty-unit membership alone
// isn't enough (e.g. a plain "Employee - FICS" position shouldn't unlock it).
export const isDeanOrChairPositionName = (positionName) => {
  const name = positionName?.toLowerCase() || "";
  return name.includes("dean") || name.includes("chair");
};

// Program Chairs may only approve FSR — TR/COS/CWA are Dean-level (or
// non-faculty approver) territory. Used to exclude chair positions from
// those three approve-as pickers, regardless of what other positions the
// same person holds.
export const isProgramChairPositionName = (positionName) => {
  const name = positionName?.toLowerCase() || "";
  return name.includes("program chair");
};

// Dean is the only position that spans both FSR (final approver) and
// TRL/COS/CWA (via the approver_id chain) — used to conditionally show the
// AO/FacSec/Both scope picker when granting AO Access, since every other
// approver position is already single-purpose.
export const isDeanPositionName = (positionName) => {
  const name = positionName?.toLowerCase() || "";
  return name.includes("dean");
};

// Stricter version of the above: the position must ALSO belong to one of
// the 3 faculties (FEd/FICS/FMDS) — a "Dean"/"Chair"-titled position in a
// non-faculty office (if one ever exists) shouldn't unlock FSR spectating.
export const isFsrSpectatablePosition = (position = {}) =>
  isDeanOrChairPositionName(position?.position_name) &&
  FACULTY_UNIT_ACRONYMS.includes(mapUnitToAcronym(position?.unit));

export const formatPositionLabel = (position = {}) => {
  const normalized = toUiPosition(position);
  const baseLabel =
    normalized?.complete_position_name || normalized?.position_name || "";

  if (!normalized.is_inactive) {
    return baseLabel;
  }

  return `${baseLabel} (inactive: ${formatAssignmentRange(
    normalized.position_start_date,
    normalized.position_end_date
  )})`;
};

export const formatFormValues = (values, config) => {
  const base = { ...values };

  if (config.requiresPosition) {
    const positions = Array.isArray(values?.positions)
      ? values.positions
          .map((position) =>
            typeof position === "object"
              ? {
                  position_id: position?.position_id,
                  position_start_date: position?.position_start_date || null,
                  position_end_date: position?.position_end_date || null,
                }
              : {
                  position_id: position,
                  position_start_date: null,
                  position_end_date: null,
                }
          )
          .filter(
            (position) =>
              Number.isInteger(Number(position.position_id)) &&
              Number(position.position_id) > 0
          )
          .map((position) => ({
            ...position,
            position_id: Number(position.position_id),
          }))
      : [];

    base.positions = positions;
    delete base.position_ids;
  }

  return base;
};

export const parseTableData = (response, tableType) => {
  const base = { ...response };

  if (tableType === "adminEmployee") {
    return base.employee || [];
  }

  if (tableType === "adminPosition") {
    return response;
  }

  return base;
};

export const getTermSortValue = (term) => {
  if (!term) return -Infinity;

  const termMatch = term.match(/(1st|2nd|3rd)/);
  const yearMatch = term.match(/AY (\d{4})-\d{4}/);

  if (!termMatch || !yearMatch) return -Infinity;

  const termMap = { "1st": 1, "2nd": 2, "3rd": 3 };

  return parseInt(yearMatch[1], 10) * 10 + termMap[termMatch[1]];
};

export const sortTermsAsc = (terms) => {
  return [...terms].sort((a, b) => getTermSortValue(a) - getTermSortValue(b));
};

export const sortTermOptionsDesc = (terms) => {
  return [...terms].sort(
    (a, b) => getTermSortValue(b.label) - getTermSortValue(a.label),
  );
};

export const sortItemsByTermAsc = (data) => {
  return [...data].sort(
    (a, b) =>
      getTermSortValue(a.academic_term) - getTermSortValue(b.academic_term),
  );
};

export const sortItemsByTermDesc = (data) => {
  return [...data].sort(
    (a, b) =>
      getTermSortValue(b.academic_term) - getTermSortValue(a.academic_term),
  );
};
